import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Mail, School } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SiteHeader } from "@/components/SiteHeader";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact SmartEssay — Email the team" },
      { name: "description", content: "Reach the SmartEssay team with questions, feedback, or help requests. We reply personally to every email from students, parents, and educators." },
      { property: "og:title", content: "Contact SmartEssay" },
      { property: "og:description", content: "Get in touch with the SmartEssay team — we reply personally to every email." },
      { property: "og:url", content: "https://smartessay.ai/contact" },
      { name: "twitter:title", content: "Contact SmartEssay" },
      { name: "twitter:description", content: "We reply personally to every email." },
    ],
    links: [{ rel: "canonical", href: "https://smartessay.ai/contact" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ContactPage",
        url: "https://smartessay.ai/contact",
        mainEntity: {
          "@type": "Organization",
          name: "SmartEssay",
          url: "https://smartessay.ai",
          contactPoint: {
            "@type": "ContactPoint",
            email: "support@tilt.university",
            contactType: "customer support",
            availableLanguage: ["English"],
          },
        },
      }),
    }],
  }),
  component: ContactPage,
});

function ContactPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const navigate = useNavigate();

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const body = `From: ${name} <${email}>\n\n${message}`;
    const href = `mailto:support@tilt.university?subject=${encodeURIComponent(
      subject || "SmartEssay question",
    )}&body=${encodeURIComponent(body)}`;
    // Open the user's email client without navigating away from the app
    window.open(href, "_blank");
    // Redirect back to the app
    navigate({ to: "/app" });
  }

  return (
    <div className="min-h-screen bg-hero-gradient">
      <SiteHeader />

      <main className="mx-auto max-w-3xl px-6 pb-24">
        <div className="text-center">
          <p className="text-xs font-medium uppercase tracking-[0.22em] text-primary">Get in touch</p>
          <div className="mx-auto mt-3 h-px w-16 bg-gold/70" aria-hidden />
          <h1 className="mt-5 font-display text-4xl font-medium leading-tight tracking-tight sm:text-5xl">
            Contact <em>us</em>
          </h1>
          <p className="mx-auto mt-3 max-w-lg text-muted-foreground">
            Have a question, feedback, or need help? We would love to hear from you.
          </p>
        </div>

        <div className="mt-10 grid gap-5 sm:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <Mail className="h-5 w-5 text-primary" />
            </div>
            <h2 className="mt-3 font-display text-lg font-semibold">General support</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Account, billing, or anything else.
            </p>
            <a
              href="mailto:support@tilt.university"
              className="mt-3 inline-block text-sm font-medium text-primary hover:underline"
            >
              support@tilt.university
            </a>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <School className="h-5 w-5 text-primary" />
            </div>
            <h2 className="mt-3 font-display text-lg font-semibold">School partnerships</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              FERPA-compliant licensing, DPAs, and bulk plans.
            </p>
            <a
              href="mailto:support@tilt.university"
              className="mt-3 inline-block text-sm font-medium text-primary hover:underline"
            >
              support@tilt.university
            </a>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="mt-8 rounded-2xl border border-border bg-card p-6 shadow-soft sm:p-8"
        >
          <h2 className="font-display text-xl font-semibold">Send us a message</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Fill in the form and your email app will open with the message ready to send.
          </p>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="c-name">Name</Label>
              <Input id="c-name" required value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="c-email">Email</Label>
              <Input
                id="c-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>
          <div className="mt-4 space-y-1.5">
            <Label htmlFor="c-subject">Subject</Label>
            <Input
              id="c-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="How can we help?"
            />
          </div>
          <div className="mt-4 space-y-1.5">
            <Label htmlFor="c-message">Message</Label>
            <Textarea
              id="c-message"
              required
              rows={6}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              Prefer email? Write directly to{" "}
              <a href="mailto:support@tilt.university" className="text-primary hover:underline">
                support@tilt.university
              </a>
              .
            </p>
            <Button type="submit" className="shadow-glow">Send message</Button>
          </div>
        </form>

        <div className="mt-8 text-center">
          <Button asChild variant="outline">
            <Link to="/help">Visit the Help Center</Link>
          </Button>
        </div>
      </main>

    </div>
  );
}
